import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Ati } from '@atlassian/ari/ati';

import {
	type ExperimentExperience,
	type LinkedEntityGranularity,
	mutuallyExclusiveBackendSearchLogicParametersSchema,
} from '../../../schemas/dynamic-config';
import type {
	BackendExperiment,
	ExperimentConfig,
	MultiLayerBackendExperiment,
	MutuallyExclusiveDynamicExperiment,
} from '../../../types';
import {
	DEFAULT_STATSIG_PARALLEL_EXPERIMENTS_CONFIG,
	STATSIG_FULL_PAGE_SEARCH_LAYER,
	STATSIG_QUICK_FIND_LAYER,
	STATSIG_SEARCH_BACKEND_FEATURE_GATE_L1_STANDARD_VS_PREMIUM,
	STATSIG_SEARCH_BACKEND_FEATURE_GATE_L2_STANDARD_VS_PREMIUM,
	STATSIG_SEARCH_BACKEND_FEATURE_GATE_L3_ENABLED,
	STATSIG_SEARCH_BACKEND_FEATURE_GATE_QUERY_EXPANSION_ENABLED,
	STATSIG_SEARCH_BACKEND_L1_LAYER_PREMIUM,
	STATSIG_SEARCH_BACKEND_L1_LAYER_STANDARD,
	STATSIG_SEARCH_BACKEND_L2_BLENDING_LAYER,
	STATSIG_SEARCH_BACKEND_L2_LAYER_PREMIUM,
	STATSIG_SEARCH_BACKEND_L2_LAYER_STANDARD,
	STATSIG_SEARCH_BACKEND_L3_LAYER,
	STATSIG_SEARCH_BACKEND_NON_OVERLAPPING_DOMAIN,
	STATSIG_SEARCH_BACKEND_QUERY_EXPANSION_LAYER,
} from '../../constants';
import {
	type BackendExperimentLayerOverride,
	mapDynamicExperiments,
	mapMutuallyExclusiveExperiment,
	mergeBackendExperimentLayerOverrides,
} from '../dynamic';
import type {
	GetLayerValuesFn,
	ProductDataRegions,
	ProductEdition,
	ThirdPartyFiltersForExperiment,
} from '../dynamic/types';

export const getBackendExperimentConfig: (
	onErrorFn?: (error: any) => any,
	shouldNotGetBackendExperiment?: boolean,
	getLayerValuesFn?: GetLayerValuesFn,
) => MultiLayerBackendExperiment | undefined = (
	onErrorFn?: (error: any) => any,
	shouldNotGetBackendExperiment: boolean = false,
	getLayerValuesFn?: GetLayerValuesFn,
) => {
	if (shouldNotGetBackendExperiment) {
		return;
	}

	return getParallelBackendExperimentsConfig(onErrorFn, getLayerValuesFn ?? getLayerValues);
};

export const getLayerValues: (
	layerName: string,
	options?: { fireLayerExposure?: boolean },
) => {
	testId?: string | null | undefined;
	name: string;
	layerId: string | null;
	shadowId: string | null;
	controlId: string | null;
	abTestId: string | null;
	armId: string | null;
} = (layerName: string, options?: { fireLayerExposure?: boolean }) => {
	const layer = FeatureGates.getLayer(layerName, {
		...(options?.fireLayerExposure === false ? { fireLayerExposure: false } : {}),
	});

	const layerNameWithoutSuffix = layerName.replace('_standard', '').replace('_premium', '');
	return {
		name: layerNameWithoutSuffix,
		layerId: layer.get<string | null>('layerId', 'default'),
		shadowId: layer.get<string | null>('shadowId', null),

		// The below values might not be sent to the backend but we can still track them in the frontend
		controlId: layer.get<string | null>('controlId', null),
		abTestId: layer.get<string | null>('abTestId', 'no_ab_test_id'),
		armId: layer.get<string | null>('armId', null),
		...(FeatureGates.checkGate('search-experiment-overrides-backend') && {
			testId: layer.get<string | null>('testId', null),
		}),
	};
};

export const getMutuallyExclusiveDynamicExperimentParameters: (
	onError?: (error: Error) => void,
	fireLayerExposure?: boolean,
	getLayerValuesFn?: GetLayerValuesFn,
) => MutuallyExclusiveDynamicExperiment = (
	onError?: (error: Error) => void,
	fireLayerExposure: boolean = true,
	getLayerValuesFn?: GetLayerValuesFn,
): MutuallyExclusiveDynamicExperiment => {
	try {
		const layer = FeatureGates.getLayer(
			STATSIG_SEARCH_BACKEND_NON_OVERLAPPING_DOMAIN,
			fireLayerExposure ? {} : { fireLayerExposure: false },
		);

		let abTestId: string | null;
		let armId: string | null;
		let rawLayerFunctions: string[] | null;

		if (getLayerValuesFn) {
			const layerResult = getLayerValuesFn(
				STATSIG_SEARCH_BACKEND_NON_OVERLAPPING_DOMAIN,
				fireLayerExposure ? {} : { fireLayerExposure: false },
			);
			abTestId = layerResult.abTestId ?? 'no_ab_test_id';
			armId = layerResult.armId;
			rawLayerFunctions = layerResult.layerFunctions ?? [];
		} else {
			abTestId = layer.get<string | null>('abTestId', 'no_ab_test_id');
			rawLayerFunctions = layer.get<string[] | null>('layerFunctions', []);
			armId = layer.get<string | null>('armId', null);
		}

		const layerFunctions =
			mutuallyExclusiveBackendSearchLogicParametersSchema.safeParse(rawLayerFunctions).data;

		return {
			layerFunctions: layerFunctions || [],
			abTestId,
			armId,
			// The get 'mutualExclusion' is breaking in Jira when retrieving a boolean value from layer,
			// so for the compatibility, we use the abTestId to determine if it's a mutually exclusive experiment
			mutualExclusion: abTestId !== 'no_ab_test_id',
		};
	} catch (error) {
		onError?.(error as Error);
	}
};

const getStandardOrPremiumLayerValues = (
	featureGateName: string,
	standardLayerName: string,
	premiumLayerName: string,
	resolveLayerValues: GetLayerValuesFn = getLayerValues,
) => {
	const isPremium = FeatureGates.checkGate(featureGateName);
	if (isPremium) {
		return resolveLayerValues(premiumLayerName);
	}
	return resolveLayerValues(standardLayerName);
};

const getParallelBackendExperimentsConfig = (
	onErrorFn?: (error: any) => any,
	resolveLayerValues: GetLayerValuesFn = getLayerValues,
): MultiLayerBackendExperiment | undefined => {
	try {
		const allLayers = [];
		const layer1 = getStandardOrPremiumLayerValues(
			STATSIG_SEARCH_BACKEND_FEATURE_GATE_L1_STANDARD_VS_PREMIUM,
			STATSIG_SEARCH_BACKEND_L1_LAYER_STANDARD,
			STATSIG_SEARCH_BACKEND_L1_LAYER_PREMIUM,
			resolveLayerValues,
		);
		allLayers.push(layer1);

		const layer2 = getStandardOrPremiumLayerValues(
			STATSIG_SEARCH_BACKEND_FEATURE_GATE_L2_STANDARD_VS_PREMIUM,
			STATSIG_SEARCH_BACKEND_L2_LAYER_STANDARD,
			STATSIG_SEARCH_BACKEND_L2_LAYER_PREMIUM,
			resolveLayerValues,
		);
		allLayers.push(layer2);
		const layer2Blending = resolveLayerValues(STATSIG_SEARCH_BACKEND_L2_BLENDING_LAYER);
		allLayers.push(layer2Blending);

		const isLayer3Enabled = FeatureGates.checkGate(STATSIG_SEARCH_BACKEND_FEATURE_GATE_L3_ENABLED);
		if (isLayer3Enabled) {
			const layer3 = resolveLayerValues(STATSIG_SEARCH_BACKEND_L3_LAYER);
			allLayers.push(layer3);
		}

		const isQueryExpansionLayerEnabled = FeatureGates.checkGate(
			STATSIG_SEARCH_BACKEND_FEATURE_GATE_QUERY_EXPANSION_ENABLED,
		);
		if (isQueryExpansionLayerEnabled) {
			const layerQueryExpansion = resolveLayerValues(STATSIG_SEARCH_BACKEND_QUERY_EXPANSION_LAYER);
			allLayers.push(layerQueryExpansion);
		}

		const concatenatedExperimentId = allLayers.map((l) => l?.layerId).join('_');
		const concatenatedShadowExperimentId = allLayers.map((l) => l?.shadowId).join('_');
		return {
			experimentId: concatenatedExperimentId,
			shadowExperimentId: concatenatedShadowExperimentId,
			experimentLayers: allLayers,
		};
	} catch (error) {
		onErrorFn?.(error);
	}

	return DEFAULT_STATSIG_PARALLEL_EXPERIMENTS_CONFIG;
};

const isEnrolledInMutuallyExclusiveUIExperiment = (experience?: ExperimentExperience) => {
	const isEnrolledInQuickFindUIExperiment =
		FeatureGates.getLayerValue(STATSIG_QUICK_FIND_LAYER, 'frontendExperimentName', '', {
			fireLayerExposure: false,
		}) !== '';
	const isEnrolledInSearchPageUIExperiment =
		FeatureGates.getLayerValue(STATSIG_FULL_PAGE_SEARCH_LAYER, 'frontendExperimentName', '', {
			fireLayerExposure: false,
		}) !== '';
	return (
		(experience === 'quick-find' && isEnrolledInQuickFindUIExperiment) ||
		(experience === 'search-page' && isEnrolledInSearchPageUIExperiment)
	);
};

const applyBackendExperimentOverrides = (
	backendExperiment: BackendExperiment | undefined,
	backendExperimentOverrides?: BackendExperimentLayerOverride[],
): BackendExperiment | undefined => {
	// Handle undefined backendExperiment by creating a minimal experiment with empty layers
	const experiment = backendExperiment || { experimentLayers: [] };
	const enableRovoSearchOverrides = Boolean(
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		FeatureGates.getExperimentValue('enable-rovo_search_overrides', 'value', false),
	);

	const shouldApplyOverrides =
		enableRovoSearchOverrides && Boolean(backendExperimentOverrides?.length);

	if (!shouldApplyOverrides) {
		return backendExperiment;
	}

	return {
		...experiment,
		experimentLayers: mergeBackendExperimentLayerOverrides(
			experiment.experimentLayers ?? [],
			backendExperimentOverrides ?? [],
		),
	};
};

/**
 * Helper function to apply overrides when normal experiment flow is not applicable.
 * Returns undefined if no overrides are provided, otherwise applies overrides to the backend experiment.
 */
const applyOverridesOrReturnUndefined = (
	experimentConfig: ExperimentConfig,
	backendExperimentOverrides?: BackendExperimentLayerOverride[],
): BackendExperiment | undefined => {
	const enableRovoSearchOverrides = Boolean(
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		FeatureGates.getExperimentValue('enable-rovo_search_overrides', 'value', false),
	);
	const shouldApplyOverrides =
		enableRovoSearchOverrides && Boolean(backendExperimentOverrides?.length);

	if (!shouldApplyOverrides) {
		return undefined;
	}

	return applyBackendExperimentOverrides(
		experimentConfig.backendExperiment,
		backendExperimentOverrides,
	);
};

export const getBackendExperiment: ({
	experimentConfig,
	entities,
	productEdition,
	isHello,
	productDataRegions,
	experience,
	tenantId,
	isJiraIssueIndexed,
	linkedEntityGranularity,
	thirdPartyFilters,
	backendExperimentOverrides,
	getLayerValuesFn,
}: {
	experimentConfig: ExperimentConfig;
	entities: string[];
	productEdition?: ProductEdition;
	isHello: boolean;
	productDataRegions?: ProductDataRegions;
	experience?: ExperimentExperience;
	tenantId?: string;
	isJiraIssueIndexed?: boolean;
	linkedEntityGranularity?: LinkedEntityGranularity;
	thirdPartyFilters?: ThirdPartyFiltersForExperiment;
	backendExperimentOverrides?: BackendExperimentLayerOverride[];
	/** Optional layer value resolver — defaults to the browser FeatureGates implementation.
	 *  SSR can inject its own adapter to resolve layer values from the node client. */
	getLayerValuesFn?: GetLayerValuesFn;
}) => BackendExperiment = ({
	experimentConfig,
	entities,
	productEdition,
	isHello,
	productDataRegions,
	experience,
	tenantId,
	isJiraIssueIndexed,
	linkedEntityGranularity,
	thirdPartyFilters,
	backendExperimentOverrides,
	getLayerValuesFn,
}: {
	experimentConfig: ExperimentConfig;
	entities: string[];
	productEdition?: ProductEdition;
	isHello: boolean;
	productDataRegions?: ProductDataRegions;
	experience?: ExperimentExperience;
	tenantId?: string;
	isJiraIssueIndexed?: boolean;
	linkedEntityGranularity?: LinkedEntityGranularity;
	thirdPartyFilters?: ThirdPartyFiltersForExperiment;
	backendExperimentOverrides?: BackendExperimentLayerOverride[];
	getLayerValuesFn?: GetLayerValuesFn;
}) => {
	if (!experimentConfig.dynamicExperiments) {
		return applyOverridesOrReturnUndefined(experimentConfig, backendExperimentOverrides);
	}

	if (isEnrolledInMutuallyExclusiveUIExperiment(experience)) {
		return applyOverridesOrReturnUndefined(experimentConfig, backendExperimentOverrides);
	}

	const requestedEntities = entities.flatMap((entity) => {
		try {
			const { resourceOwner, resourceType } = Ati.parse(entity);
			return [{ entity: resourceOwner, subEntity: resourceType.toUpperCase() }];
		} catch {
			return [];
		}
	});

	// Skip layer exposure when overrides are enabled and there are overrides.
	const enableRovoSearchOverrides = Boolean(
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		FeatureGates.getExperimentValue('enable-rovo_search_overrides', 'value', false),
	);
	const skipLayerExposure =
		enableRovoSearchOverrides && Boolean(backendExperimentOverrides?.length);
	const fireLayerExposure = !skipLayerExposure;

	const experimentParams = getMutuallyExclusiveDynamicExperimentParameters(
		undefined,
		fireLayerExposure,
		getLayerValuesFn,
	);
	if (experimentParams?.mutualExclusion) {
		return applyBackendExperimentOverrides(
			mapMutuallyExclusiveExperiment({
				experimentParams,
				productEdition: productEdition,
				isHello,
				requestedEntities,
				productDataRegions: productDataRegions,
				experience,
				tenantId,
				isJiraIssueIndexed,
				linkedEntityGranularity,
				thirdPartyFilters,
			}),
			backendExperimentOverrides,
		);
	}

	return applyBackendExperimentOverrides(
		mapDynamicExperiments({
			dynamicExperiments: experimentConfig.dynamicExperiments,
			productEdition: productEdition,
			isHello,
			requestedEntities,
			productDataRegions: productDataRegions,
			experience,
			tenantId,
			isJiraIssueIndexed,
			linkedEntityGranularity,
			thirdPartyFilters,
			fireLayerExposure,
			getLayerValuesFn,
		}),
		backendExperimentOverrides,
	);
};
